import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Square, RotateCcw, Save, Plus, Keyboard,
  RefreshCw, CheckCircle2, Trophy
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

export default function ScorerEntryPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [servingSide, setServingSide] = useState('home'); // 'home' | 'away'
  const [finishOpen, setFinishOpen] = useState(false);
  const [winner, setWinner] = useState(null); // 'home' | 'away'
  const [finishing, setFinishing] = useState(false);

  // Refs for stable keyboard handler
  const matchRef = useRef(match);
  const scoreHistoryRef = useRef(scoreHistory);
  matchRef.current = match;
  scoreHistoryRef.current = scoreHistory;

  useEffect(() => {
    fetchMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, activeOrgSlug]);

  const fetchMatch = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/scorer/matches/${matchId}`);
      let matchData = { ...response.data };

      // Scorer match often has home_team/away_team with id: null — enrich from fixtures
      if (!matchData.home_team?.name && matchData.tournament_id) {
        try {
          const fxRes = await api.get(`/orgs/${orgSlug}/tournaments/${matchData.tournament_id}/fixtures`);
          const fixtures = Array.isArray(fxRes.data) ? fxRes.data : fxRes.data.items || [];
          const fx = fixtures.find(f => f.match_id === matchId || f.id === matchId);
          if (fx) {
            matchData.home_team = { ...matchData.home_team, name: fx.home?.name, short_name: fx.home?.short_name };
            matchData.away_team = { ...matchData.away_team, name: fx.away?.name, short_name: fx.away?.short_name };
            if (!matchData.sport) matchData.sport = fx.sport_code?.replace(/_/g, ' ');
          }
        } catch (_) {
          // Non-fatal: team name enrichment failed, use existing data
        }
      }

      setMatch(matchData);
    } catch (error) {
      toast.error('Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  // Score helpers
  const getScores = useCallback((m) => ({
    s1: m?.score1 ?? m?.home_score ?? 0,
    s2: m?.score2 ?? m?.away_score ?? 0,
  }), []);

  const updateScore = useCallback((side, pts) => {
    const m = matchRef.current;
    if (!m || m.status === 'completed' || m.status === 'finished') return;
    const { s1, s2 } = getScores(m);
    setScoreHistory((h) => [...h, { score1: s1, score2: s2 }]);
    const newS1 = side === 'home' ? s1 + pts : s1;
    const newS2 = side === 'away' ? s2 + pts : s2;
    setMatch((prev) => ({ ...prev, score1: newS1, score2: newS2, home_score: newS1, away_score: newS2 }));
    setLastSaved(new Date());
    // Auto-switch server after point (optional sport behaviour)
    // setServingSide((s) => (s === 'home' ? 'away' : 'home'));
  }, [getScores]);

  const handleUndo = useCallback(() => {
    const hist = scoreHistoryRef.current;
    if (hist.length === 0) return;
    const prev = hist[hist.length - 1];
    setScoreHistory((h) => h.slice(0, -1));
    setMatch((m) => ({ ...m, score1: prev.score1, score2: prev.score2, home_score: prev.score1, away_score: prev.score2 }));
    toast.success('Score reverted');
  }, []);

  const handleStartMatch = useCallback(async () => {
    const m = matchRef.current;
    if (!m || m.status === 'live') return;
    try {
      setSaving(true);
      await api.post(`/orgs/${orgSlug}/matches/${matchId}/status`, { status: 'live' });
      setMatch((prev) => ({ ...prev, status: 'live' }));
      toast.success('Match started!');
    } catch {
      toast.error('Failed to start match');
    } finally {
      setSaving(false);
    }
  }, [activeOrgSlug, matchId]);

  // Keyboard shortcuts — stable via refs
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const m = matchRef.current;
      if (!m) return;
      switch (e.key) {
        case '1': updateScore('home', 1); break;
        case '2': updateScore('away', 1); break;
        case '3': updateScore('home', 2); break;
        case '4': updateScore('away', 2); break;
        case 'u': case 'U': handleUndo(); break;
        case 's': case 'S': if (!e.ctrlKey && !e.metaKey) handleStartMatch(); break;
        case 'v': case 'V': setServingSide((s) => (s === 'home' ? 'away' : 'home')); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [updateScore, handleUndo, handleStartMatch]);

  const handleConfirmFinish = async () => {
    if (!winner) { toast.error('Please select the winner'); return; }
    const m = matchRef.current;
    const { s1, s2 } = getScores(m);
    const team1 = m?.team1 || m?.home_team || {};
    const team2 = m?.team2 || m?.away_team || {};
    const winnerId = winner === 'home' ? (team1.id || null) : (team2.id || null);

    setFinishing(true);
    try {
      await api.post(`/orgs/${orgSlug}/matches/${matchId}/status`, { status: 'completed' });
      if (winnerId) {
        await api.post(`/orgs/${orgSlug}/matches/${matchId}/result`, {
          winner_type: 'team',
          winner_id: winnerId,
          is_draw: false,
          final_score: { home: s1, away: s2 },
        }).catch(() => {}); // non-fatal if result fails
      }
      setMatch((prev) => ({ ...prev, status: 'completed' }));
      setFinishOpen(false);
      toast.success('Match completed!');
    } catch {
      toast.error('Failed to finish match');
    } finally {
      setFinishing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <Skeleton className="h-8 w-32 bg-[#1d2230] mb-6" />
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-8">
              <Skeleton className="h-16 w-16 rounded-full bg-[#1d2230]" />
              <Skeleton className="h-20 w-32 bg-[#1d2230]" />
              <Skeleton className="h-16 w-16 rounded-full bg-[#1d2230]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-14 bg-[#1d2230] rounded-lg" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <p className="text-[#8a93a3]">Match not found</p>
        <Button onClick={() => navigate('/score')} className="mt-4">
          Back to Matches
        </Button>
      </div>
    );
  }

  const team1 = match.team1 || match.home_team || {};
  const team2 = match.team2 || match.away_team || {};
  const { s1: score1, s2: score2 } = getScores(match);
  const isLive = match.status === 'live';
  const isCompleted = match.status === 'completed' || match.status === 'finished';
  const isScheduled = !isLive && !isCompleted;
  const needsServing = match.sport_format_spec?.needs_serving_side ?? false;

  const TeamAvatar = ({ team, size = 'lg' }) => {
    const dim = size === 'lg' ? 'h-16 w-16' : 'h-12 w-12';
    return (
      <div className={`${dim} rounded-full bg-[#1d2230] flex items-center justify-center`}>
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.name} className={`${size === 'lg' ? 'h-14 w-14' : 'h-10 w-10'} rounded-full object-cover`} />
        ) : (
          <span className="text-[#f5c518] font-bold text-xl">
            {team.short_name?.[0] || team.name?.[0] || '?'}
          </span>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col min-h-[calc(100vh-120px)]" data-testid="scorer-match-scoring">
        {/* Header */}
        <div className="p-4 sm:p-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/score')}
            className="mb-4 text-[#8a93a3] hover:text-[#f0f2f7] -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className={`border ${
                isLive      ? 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]' :
                isCompleted ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]' :
                              'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border-[rgba(59,130,246,0.35)]'
              }`}>
                {isLive && <span className="w-2 h-2 rounded-full bg-[#ef4444] animate-pulse mr-1.5 inline-block" />}
                {isLive ? 'LIVE' : isCompleted ? 'Completed' : 'Scheduled'}
              </Badge>
              <span className="text-sm text-[#8a93a3]">
                {match.sport || ''}{match.category ? ` · ${match.category}` : ''}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={fetchMatch}
                    className="text-[#8a93a3] hover:text-[#f0f2f7]"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-[#161a26] border-[#252a37]">Refresh</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-[#8a93a3] hover:text-[#f0f2f7]">
                    <Keyboard className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-[#161a26] border-[#252a37] p-3 space-y-1 text-xs">
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">1</kbd> +1 Home</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">2</kbd> +1 Away</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">3</kbd> +2 Home</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">4</kbd> +2 Away</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">U</kbd> Undo</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">S</kbd> Start</p>
                  <p><kbd className="bg-[#1d2230] px-1.5 rounded font-mono">V</kbd> Switch server</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Score Panel */}
        <div className="flex-1 px-4 sm:px-6">
          <Card className="bg-[#161a26] border-[#252a37] rounded-[18px]" data-testid="score-entry-score-panel">
            <CardContent className="p-6">
              {/* Teams & Score */}
              <div className="flex items-center justify-between mb-8">
                {/* Team 1 (Home) */}
                <div className="text-center flex-1">
                  <div className="relative mx-auto w-fit mb-3">
                    <TeamAvatar team={team1} size="lg" />
                    {(needsServing || true) && servingSide === 'home' && isLive && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#f5c518] border-2 border-[#06080d]" title="Serving" />
                    )}
                  </div>
                  <p className="font-semibold text-[#f0f2f7] text-sm sm:text-base">
                    {team1.short_name || team1.name || 'Home'}
                  </p>
                </div>

                {/* Score */}
                <div className="px-4 sm:px-8 text-center">
                  <div className={`text-5xl sm:text-6xl font-bold tabular-nums ${
                    isLive ? 'text-[#f5c518]' : 'text-[#f0f2f7]'
                  }`}>
                    <span>{score1}</span>
                    <span className="text-[#8a93a3] mx-2 sm:mx-3 text-4xl">:</span>
                    <span>{score2}</span>
                  </div>
                  {lastSaved && (
                    <p className="text-xs text-[#8a93a3] mt-2 flex items-center justify-center gap-1">
                      <Save className="h-3 w-3" />
                      {lastSaved.toLocaleTimeString()}
                    </p>
                  )}
                </div>

                {/* Team 2 (Away) */}
                <div className="text-center flex-1">
                  <div className="relative mx-auto w-fit mb-3">
                    <TeamAvatar team={team2} size="lg" />
                    {(needsServing || true) && servingSide === 'away' && isLive && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#f5c518] border-2 border-[#06080d]" title="Serving" />
                    )}
                  </div>
                  <p className="font-semibold text-[#f0f2f7] text-sm sm:text-base">
                    {team2.short_name || team2.name || 'Away'}
                  </p>
                </div>
              </div>

              {/* Score Buttons */}
              {!isCompleted && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Button
                      onClick={() => updateScore('home', 1)}
                      disabled={!isLive || saving}
                      className="w-full h-14 bg-[#1d2230] hover:bg-[#252a37] text-[#f0f2f7] text-lg font-semibold disabled:opacity-40 transition-all active:scale-95"
                      data-testid="score-entry-point-button-1"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      +1 Home
                    </Button>
                    <Button
                      onClick={() => updateScore('home', 2)}
                      disabled={!isLive || saving}
                      className="w-full h-14 bg-[#1d2230] hover:bg-[#252a37] text-[#f0f2f7] text-lg font-semibold disabled:opacity-40 transition-all active:scale-95"
                      data-testid="score-entry-point-button-3"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      +2 Home
                    </Button>
                  </div>
                  <div className="space-y-3">
                    <Button
                      onClick={() => updateScore('away', 1)}
                      disabled={!isLive || saving}
                      className="w-full h-14 bg-[#1d2230] hover:bg-[#252a37] text-[#f0f2f7] text-lg font-semibold disabled:opacity-40 transition-all active:scale-95"
                      data-testid="score-entry-point-button-2"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      +1 Away
                    </Button>
                    <Button
                      onClick={() => updateScore('away', 2)}
                      disabled={!isLive || saving}
                      className="w-full h-14 bg-[#1d2230] hover:bg-[#252a37] text-[#f0f2f7] text-lg font-semibold disabled:opacity-40 transition-all active:scale-95"
                      data-testid="score-entry-point-button-4"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      +2 Away
                    </Button>
                  </div>
                </div>
              )}

              {isCompleted && (
                <div className="text-center py-4">
                  <CheckCircle2 className="h-12 w-12 text-[#22c55e] mx-auto mb-3" />
                  <p className="text-lg font-semibold text-[#22c55e]">Match Completed</p>
                  <p className="text-[#8a93a3] text-sm mt-1">Final score: {score1} : {score2}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sticky Action Bar */}
        {!isCompleted && (
          <div className="sticky bottom-0 z-30 bg-[#10131c]/95 backdrop-blur border-t border-[#252a37] p-4">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              {/* Undo */}
              <Button
                onClick={handleUndo}
                disabled={scoreHistory.length === 0 || saving}
                variant="outline"
                className="h-12 border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26] disabled:opacity-40 px-4"
                data-testid="score-entry-undo-button"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Undo
              </Button>

              {/* Switch Server */}
              {isLive && (
                <Button
                  onClick={() => setServingSide((s) => (s === 'home' ? 'away' : 'home'))}
                  variant="outline"
                  className="h-12 border-[#252a37] text-[#f5c518] hover:bg-[#161a26] px-4"
                  data-testid="score-entry-switch-server-button"
                >
                  Switch Server
                </Button>
              )}

              {/* Start / Finish */}
              {isScheduled && (
                <Button
                  onClick={handleStartMatch}
                  disabled={saving}
                  className="flex-1 h-12 bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold"
                  data-testid="score-entry-start-button"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Match
                </Button>
              )}
              {isLive && (
                <Button
                  onClick={() => { setWinner(null); setFinishOpen(true); }}
                  disabled={saving}
                  className="flex-1 h-12 bg-[#ef4444] hover:bg-[#dc2626] text-white font-semibold"
                  data-testid="score-entry-finish-button"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Finish Match
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Finish Match Dialog */}
      <Dialog open={finishOpen} onOpenChange={setFinishOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37]" data-testid="finish-match-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Finish Match</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-[#f5c518] tabular-nums mb-2">
                {score1} : {score2}
              </p>
              <p className="text-sm text-[#8a93a3]">Final Score</p>
            </div>
            <div>
              <p className="text-sm font-medium text-[#8a93a3] mb-3">Select winner</p>
              <div className="grid grid-cols-2 gap-3">
                {[{ side: 'home', team: team1 }, { side: 'away', team: team2 }].map(({ side, team }) => (
                  <div
                    key={side}
                    onClick={() => setWinner(side)}
                    className={`p-4 rounded-lg border-2 cursor-pointer text-center transition-colors ${
                      winner === side
                        ? 'border-[#f5c518] bg-[rgba(245,197,24,0.1)]'
                        : 'border-[#252a37] hover:border-[#8a93a3]'
                    }`}
                    data-testid={`finish-winner-${side}`}
                  >
                    <Trophy className={`h-6 w-6 mx-auto mb-2 ${winner === side ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`} />
                    <p className={`font-medium text-sm ${winner === side ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}>
                      {team.short_name || team.name || (side === 'home' ? 'Home' : 'Away')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinishOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmFinish}
              disabled={finishing || !winner}
              className="bg-[#ef4444] hover:bg-[#dc2626] text-white"
              data-testid="finish-match-confirm-button"
            >
              {finishing ? 'Finishing...' : 'Confirm & Finish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
